import { ApolloError, FetchResult } from "@apollo/client";
import type { GraphQLFormattedError } from "graphql";

export interface ErrorDetail {
  message: string;
  title: string;
}

export interface RecoverableError {
  __typename?: "RecoverableError";
  message?: string | null;
  title?: string | null;
}

export interface MutationPayloadWithErrors {
  errors?: RecoverableError[] | null;
  [key: string]: unknown;
}

/**
 * A utility class for standardizing error handling across Apollo GraphQL mutations.
 *
 * Handles three types of errors:
 * 1. GraphQL/Network errors - Connection issues, server errors, validation failures
 * 2. Mutation-specific errors - RecoverableError[] returned in the mutation payload
 * 3. Exception errors - JavaScript errors thrown during mutation execution
 *
 * @example
 * ```typescript
 * // Handle mutation result
 * const errorHandler = MutationErrorHandler.fromResult(result, result.errors);
 * return errorHandler.toResult();
 *
 * // Handle caught exception
 * const errorHandler = MutationErrorHandler.fromException(err);
 * return errorHandler.toResult();
 *
 * // Check for errors
 * if (errorHandler.hasErrors()) {
 *   console.log('First error:', errorHandler.getError());
 *   console.log('All errors:', errorHandler.getErrors());
 * }
 * ```
 */

export class MutationErrorHandler {
  private errors: ErrorDetail[] = [];
  private success = true;

  private constructor() {
    // Private constructor to force use of static factory methods
  }

  /**
   * Create error handler from mutation result
   */
  public static fromResult<T>(
    result?: FetchResult<T>,
    mutationErrors?: readonly GraphQLFormattedError[],
  ): MutationErrorHandler {
    const handler = new MutationErrorHandler();
    if (result || mutationErrors) {
      handler.processResult(result, mutationErrors);
    }
    return handler;
  }

  /**
   * Process Apollo mutation result and extract errors
   */
  private processResult(
    result?: FetchResult<unknown>,
    mutationErrors?: readonly GraphQLFormattedError[],
  ): void {
    if (
      (result?.data === undefined || result?.data === null) &&
      (result?.errors || mutationErrors)
    ) {
      this.handleApolloErrors(result?.errors || mutationErrors || []);
      return;
    }

    if (result?.data && typeof result.data === "object") {
      const mutationResults = Object.values(result.data);
      const mutationResult = mutationResults[0];

      if (this.hasMutationErrors(mutationResult)) {
        this.handleMutationErrors(mutationResult.errors);
        return;
      }
    }

    this.success = true;
    this.errors = [];
  }

  /**
   * Type guard to check if mutation result has errors
   */
  private hasMutationErrors(
    data: unknown,
  ): data is { errors: RecoverableError[] } {
    return (
      typeof data === "object" &&
      data !== null &&
      "errors" in data &&
      Array.isArray((data as Record<string, unknown>).errors)
    );
  }

  /**
   * Handle Apollo-level errors (network, GraphQL, etc.)
   */
  private handleApolloErrors(errors: readonly GraphQLFormattedError[]): void {
    this.success = false;

    if (errors.length === 0) {
      this.errors = [
        {
          message: "Unknown error occurred",
          title: "Mutation Error",
        },
      ];
      return;
    }

    this.errors = errors.map((error) => ({
      message: error.message || "GraphQL error occurred",
      title: "Mutation Error",
    }));
  }

  /**
   * Handle mutation-specific errors from the response
   */
  private handleMutationErrors(errors: RecoverableError[]): void {
    this.success = errors.length === 0;
    this.errors = errors.map((error) => ({
      message: error.message || "",
      title: error.title || "Mutation Error",
    }));
  }

  /**
   * Handle caught exceptions (try/catch errors)
   */
  public static fromException(err: unknown): MutationErrorHandler {
    const handler = new MutationErrorHandler();
    handler.success = false;

    let errorMessage = "Unknown error occurred";

    if (err instanceof ApolloError) {
      if (err.networkError) {
        errorMessage = `Network error: ${err.networkError.message}`;
      } else if (err.graphQLErrors.length > 0) {
        errorMessage = err.graphQLErrors[0].message;
      } else {
        errorMessage = err.message;
      }
    } else if (err instanceof Error) {
      errorMessage = err.message;
    } else if (this.isObjectWithMessage(err)) {
      if (this.hasNetworkError(err)) {
        errorMessage = `Network error: ${err.networkError.message}`;
      } else {
        errorMessage = err.message;
      }
    }

    handler.errors = [
      {
        message: errorMessage,
        title: "Mutation Error",
      },
    ];

    return handler;
  }

  /**
   * Type guard to check if error is an object with message property
   */
  private static isObjectWithMessage(err: unknown): err is { message: string } {
    return (
      typeof err === "object" &&
      err !== null &&
      "message" in err &&
      typeof (err as Record<string, unknown>).message === "string"
    );
  }

  /**
   * Type guard to check if error has networkError property
   */
  private static hasNetworkError(
    err: unknown,
  ): err is { networkError: Error; message: string } {
    return (
      typeof err === "object" &&
      err !== null &&
      "networkError" in err &&
      (err as Record<string, unknown>).networkError instanceof Error &&
      "message" in err &&
      typeof (err as Record<string, unknown>).message === "string"
    );
  }

  /**
   * Check if there are any errors
   */
  public hasErrors(): boolean {
    return !this.success || this.errors.length > 0;
  }

  /**
   * Get all errors
   */
  public getErrors(): ErrorDetail[] {
    return [...this.errors];
  }

  /**
   * Get the first error, or a default error if none exist
   */
  public getError(): ErrorDetail {
    if (this.errors.length === 0) {
      return {
        message: "Unknown error occurred",
        title: "Error",
      };
    }
    return this.errors[0];
  }

  /**
   * Check if the operation was successful
   */
  public isSuccess(): boolean {
    return this.success;
  }

  /**
   * Get a result object in the format expected by your hooks
   */
  public toResult(): { success: boolean; errors: ErrorDetail[] } {
    return {
      success: this.success,
      errors: this.errors,
    };
  }

  /**
   * Static factory method for successful operations
   */
  public static success(): MutationErrorHandler {
    const handler = new MutationErrorHandler();
    handler.success = true;
    handler.errors = [];
    return handler;
  }
}
